import { useEffect, useMemo, useState } from "react";
import API_BASE from "../config/api";

/**
 * Shared "global search suggestion" data source.
 *
 * Hits /api/searchSuggestions.php the same way HeroSection always has, so
 * every search box in the app (home hero, search-results filter bar, and
 * any future one) shares one debounced fetch + one shape of result:
 *   { categories: [{id, label}], businesses: [{id, title, rating, ...}] }
 *
 * @param {string} query        raw text currently typed
 * @param {number} minLength     minimum chars before firing a request (default 2)
 * @param {number} debounceMs    debounce delay (default 250ms)
 */
export default function useSearchSuggestions(query, { minLength = 2, debounceMs = 250 } = {}) {
    const [suggestions, setSuggestions] = useState({ categories: [], businesses: [] });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const q = (query || "").trim();
        if (q.length < minLength) {
            setSuggestions({ categories: [], businesses: [] });
            setLoading(false);
            return;
        }

        setLoading(true);
        const timer = setTimeout(() => {
            fetch(`${API_BASE}/api/searchSuggestions.php?q=${encodeURIComponent(q)}`)
                .then((res) => res.json())
                .then((data) => {
                    if (data && data.success) {
                        setSuggestions({
                            categories: data.categories || [],
                            businesses: data.businesses || [],
                        });
                    }
                })
                .catch(() => { /* network errors are non-fatal for typeahead */ })
                .finally(() => setLoading(false));
        }, debounceMs);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, minLength, debounceMs]);

    // Flat list (categories first, then businesses) for keyboard navigation
    const flatItems = useMemo(() => ([
        ...suggestions.categories.map((c) => ({ kind: "category", data: c })),
        ...suggestions.businesses.map((b) => ({ kind: "business", data: b })),
    ]), [suggestions]);

    return { suggestions, flatItems, loading };
}
